import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Flex, Heading, Text, TextField } from '@radix-ui/themes';
import { FieldError, ServerErrorCallout } from '@/components/common';
import { WizardScreen } from '@/components/layout';
import { ContinueButton, OnboardingWizardHeader } from '@/components/onboarding';
import { ProfileSchemaKey, useProfileSchema } from '@/lib/validators';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { fieldLabelStyle } from './styles';
import type { OnboardingStepProps } from './types';
import { useSaveAndNext } from './useSaveAndNext';

export function NameStep({ flow }: OnboardingStepProps) {
  const { t } = useTranslation(['onboarding', 'profile']);
  const userName = useOnboardingStore(state => state.userName);
  const setUserName = useOnboardingStore(state => state.setUserName);
  const setError = useOnboardingStore(state => state.setError);
  const error = useOnboardingStore(state => state.error);
  const saveAndNext = useSaveAndNext(flow);
  const schema = useProfileSchema([ProfileSchemaKey.NAME]);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { name: userName },
  });

  const onValid = async (data: z.infer<typeof schema>) => {
    setError(null);
    setUserName(data.name);
    await saveAndNext();
  };

  return (
    <WizardScreen header={<OnboardingWizardHeader flow={flow} />}>
      <Flex direction="column" gap="4" py="2" pb="5">
        <Heading size="5" weight="light">
          {t('steps.name.title')}
        </Heading>
        {error ? <ServerErrorCallout message={error} /> : null}
        <form id="onboarding-name-form" onSubmit={handleSubmit(onValid)} noValidate>
          <Flex direction="column" gap="1">
            <Text size="1" color="gray" style={fieldLabelStyle}>
              {t('steps.name.fieldLabel')}
            </Text>
            <TextField.Root
              placeholder={t('steps.name.placeholder')}
              aria-label={t('steps.name.fieldLabel')}
              data-testid="onboarding-name"
              aria-invalid={errors.name ? true : undefined}
              {...register('name')}
            />
            <FieldError message={errors.name?.message} />
          </Flex>
        </form>
        <ContinueButton onClick={() => void handleSubmit(onValid)()}>
          {t('actions.continue')}
        </ContinueButton>
      </Flex>
    </WizardScreen>
  );
}
